/**
 * Offline chart storage.
 *
 * Chart archives are tens of megabytes and are read by HTTP range request, so
 * they are deliberately excluded from the service-worker precache. Taking charts
 * offline is instead an explicit act: the skipper chooses an area and downloads
 * it before leaving the dock.
 *
 * That explicitness is the point. A boat loses signal exactly where navigation
 * matters, and "I think it cached while I had wifi" is not something to bet a
 * hull on. The user must be able to see what they have.
 *
 * HOW IT WORKS
 * ------------
 * PMTiles fetches byte ranges of one large file. Caching the whole archive would
 * be simplest but downloads the entire coast to sail one bay. Instead each
 * *tile* is fetched through the PMTiles reader and stored under a synthetic URL
 * (`<archive>/z/x/y`) in the Cache Storage API. `cachedTileSource` then serves
 * those back to PMTiles without a network round trip.
 *
 * Cache Storage rather than IndexedDB because it is designed for opaque binary
 * responses, survives eviction better under storage pressure, and is what the
 * service worker would use anyway.
 */

import { PMTiles, type RangeResponse, type Source } from 'pmtiles';

import type { ChartManifest } from '../chart/style';

const CACHE_NAME = 'webnav-charts-v1';

/** Geographic extent to take offline. */
export interface Region {
  south: number;
  west: number;
  north: number;
  east: number;
}

export interface DownloadProgress {
  /** Tiles written so far. */
  done: number;
  /** Tiles the plan expects to write. */
  total: number;
  /** Bytes written so far. */
  bytes: number;
  /** Archive currently being fetched. */
  file: string;
}

export interface DownloadResult {
  tiles: number;
  bytes: number;
  /** Tiles the archive simply does not contain — empty ocean, not a failure. */
  absent: number;
  failed: number;
}

/** True when this browser can store charts offline at all. */
export function isChartCacheAvailable(): boolean {
  return typeof caches !== 'undefined';
}

/** Synthetic URL a cached tile is stored under. */
export function tileCacheKey(archiveUrl: string, z: number, x: number, y: number): string {
  return `${archiveUrl}/${z}/${x}/${y}`;
}

/** Web-Mercator tile range covering a region at one zoom. */
export function tileRangeFor(region: Region, zoom: number): {
  minX: number; maxX: number; minY: number; maxY: number;
} {
  const n = 2 ** zoom;
  const lonToX = (lon: number): number => Math.floor(((lon + 180) / 360) * n);
  const latToY = (lat: number): number => {
    // Clamp to the Mercator limit; beyond it the projection is undefined.
    const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
    const rad = (clamped * Math.PI) / 180;
    return Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n);
  };

  return {
    minX: Math.max(0, lonToX(region.west)),
    maxX: Math.min(n - 1, lonToX(region.east)),
    // Latitude runs the opposite way to tile Y.
    minY: Math.max(0, latToY(region.north)),
    maxY: Math.min(n - 1, latToY(region.south)),
  };
}

/**
 * How many tiles a region needs across every band.
 *
 * Shown to the user before the download starts. A region that looks small on a
 * zoomed-out chart can be tens of thousands of tiles at harbour scale, and
 * finding that out halfway through a download on a marina wifi is unpleasant.
 */
export function estimateTileCount(manifest: ChartManifest, region: Region): number {
  let total = 0;
  for (const source of manifest.sources) {
    for (let z = source.minzoom; z <= source.maxzoom; z++) {
      const { minX, maxX, minY, maxY } = tileRangeFor(region, z);
      if (maxX < minX || maxY < minY) continue;
      total += (maxX - minX + 1) * (maxY - minY + 1);
    }
  }
  return total;
}

/**
 * A PMTiles Source that reads from the HTTP cache first.
 *
 * Range requests are cached by byte range, which is why the key includes the
 * offset and length: two different ranges of the same archive are different
 * resources.
 */
export function cachedRangeSource(url: string): Source {
  return {
    getKey: () => url,
    async getBytes(offset: number, length: number): Promise<RangeResponse> {
      // The range goes in a QUERY parameter, not a fragment. The Cache API
      // strips fragments before matching, so `#range=...` keys would all
      // collide on the same entry and hand back the wrong bytes — which reads
      // as a corrupt chart rather than as a cache bug.
      const key = rangeCacheKey(url, offset, length);

      // Cache access must never be able to break navigation. Any failure here
      // falls through to the network; a slow chart beats no chart.
      try {
        const cache = await caches.open(CACHE_NAME);
        const hit = await cache.match(key);
        if (hit) return { data: await hit.arrayBuffer() };
      } catch {
        // fall through to network
      }

      const response = await fetch(url, {
        headers: { Range: `bytes=${offset}-${offset + length - 1}` },
      });
      if (!response.ok && response.status !== 206) {
        throw new Error(`chart range request failed: HTTP ${response.status}`);
      }
      const data = await response.arrayBuffer();

      try {
        const cache = await caches.open(CACHE_NAME);
        await cache.put(key, new Response(data.slice(0)));
      } catch {
        // Storage full or unavailable — the tile still loads, just not offline.
      }
      return { data };
    },
  };
}

/** Cache key for one byte range of an archive. */
export function rangeCacheKey(url: string, offset: number, length: number): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}__range=${offset}-${offset + length - 1}`;
}

/**
 * Download every tile covering `region` for offline use.
 *
 * Failures are counted rather than thrown: one bad tile on a flaky marina
 * connection should not discard the other nine thousand. The result reports what
 * was missed so the UI can offer to resume.
 */
export async function downloadRegion(
  manifest: ChartManifest,
  baseUrl: string,
  region: Region,
  onProgress?: (progress: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<DownloadResult> {
  if (!isChartCacheAvailable()) {
    throw new Error('This browser cannot store charts offline.');
  }

  const cache = await caches.open(CACHE_NAME);
  const total = estimateTileCount(manifest, region);
  let done = 0;
  let bytes = 0;
  let absent = 0;
  let failed = 0;

  for (const source of manifest.sources) {
    const archiveUrl = `${baseUrl}${source.file}`;
    const archive = new PMTiles(cachedRangeSource(archiveUrl));

    for (let z = source.minzoom; z <= source.maxzoom; z++) {
      const { minX, maxX, minY, maxY } = tileRangeFor(region, z);
      for (let x = minX; x <= maxX; x++) {
        for (let y = minY; y <= maxY; y++) {
          if (signal?.aborted) throw new DOMException('Download cancelled', 'AbortError');

          const key = tileCacheKey(archiveUrl, z, x, y);
          try {
            if (await cache.match(key)) { done += 1; continue; }
            const tile = await archive.getZxy(z, x, y);
            if (!tile?.data) {
              // The archive has no tile here — open water, not an error.
              absent += 1;
              done += 1;
              continue;
            }
            await cache.put(key, new Response(tile.data));
            bytes += tile.data.byteLength;
            done += 1;
          } catch {
            failed += 1;
            done += 1;
          }

          if (done % 25 === 0) onProgress?.({ done, total, bytes, file: source.file });
        }
      }
    }
  }

  onProgress?.({ done, total, bytes, file: '' });
  return { tiles: done - absent - failed, bytes, absent, failed };
}

/** Bytes currently held offline, and how many entries. */
export async function cacheUsage(): Promise<{ entries: number; bytes: number }> {
  if (!isChartCacheAvailable()) return { entries: 0, bytes: 0 };
  const cache = await caches.open(CACHE_NAME);
  const keys = await cache.keys();
  let bytes = 0;
  for (const request of keys) {
    const response = await cache.match(request);
    if (response) bytes += (await response.arrayBuffer()).byteLength;
  }
  return { entries: keys.length, bytes };
}

/**
 * How much room the browser will give us, if it will say.
 * Worth showing before a large download: quotas on mobile are not generous.
 */
export async function storageEstimate(): Promise<{ usage: number; quota: number } | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();
  return { usage, quota };
}

/**
 * Ask the browser not to evict our charts under storage pressure.
 * Advisory only — browsers may refuse — but worth asking before a download the
 * user is relying on.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function clearChartCache(): Promise<void> {
  if (!isChartCacheAvailable()) return;
  await caches.delete(CACHE_NAME);
}
